import React, { FC, useEffect } from 'react';
import 'whatwg-fetch';
import Button from 'src/components/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Select,
  TextField,
} from '@material-ui/core';
import EmployeeSelect from '../display/components/EmployeeSelect';

import { Controller, useForm } from 'react-hook-form';
import { LegacyDeal } from 'src/interfaces/api/Deal';
import LegacyCustomerAutocomplete from '../../../components/ApiField/LegacyCustomerAutocomplete';
import InterlocutorsSelect from './components/InterlocutorsSelect';

interface DealDuplicateRequestBody {
  name: string;
  originId: number;
  dealId: number;
  typeId: number;
  commercialEmployeeId: number;
  interlocutorIds: Array<number>;
  customerId: number;
  desiredClotureDate: string;
}

interface Props {
  agencyId?: number;
  deal: LegacyDeal;
  originTypes: Required<LegacyDeal>['origin'][];
  dealTypes: Required<LegacyDeal>['type'][];
  onValidate: (data: DealDuplicateRequestBody) => void;
  onClose: () => void;
  isOpen: boolean;
}

const ModalDuplicateDeal: FC<Props> = (props) => {
  const { deal, agencyId, originTypes, dealTypes, isOpen, onClose, onValidate } = props;

  const { control, reset, watch, handleSubmit } = useForm<DealDuplicateRequestBody>();

  const customerId = watch('customerId');

  useEffect(() => {
    reset({
      dealId: deal.id,
      name: deal.name,
      originId: deal.origin?.id,
      typeId: deal.type?.id,
      commercialEmployeeId: deal.stepCommercial?.commercialEmployee?.id,
      interlocutorIds: deal.interlocutors?.map((interlocutor) => interlocutor.id) || [],
      customerId: deal.customer?.id,
      desiredClotureDate: undefined,
    });
  }, [deal.id]);

  const onSubmit = (data: DealDuplicateRequestBody) => {
    onValidate(data);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Dupliquer l'affaire</DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name='name'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Nom de l'affaire"
                  variant='outlined'
                  fullWidth
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {originTypes.length > 0 && (
            <Grid item xs={12}>
              <Controller
                name='originId'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field: { value, ...field }, fieldState: { error } }) => (
                  <FormControl {...field} variant='outlined' fullWidth error={undefined !== error}>
                    <InputLabel>Origine</InputLabel>
                    <Select label='Origine' value={value || ''} native>
                      {originTypes.map((origin) => (
                        <option key={origin.id} value={origin.id}>
                          {origin.labelOrigin}
                        </option>
                      ))}
                    </Select>
                    {undefined !== error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name='desiredClotureDate'
              control={control}
              rules={{
                required: { value: true, message: 'Le champ est requis' },
                pattern: {
                  value: /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/i,
                  message: 'La date doit être de la forme DD/MM/AAAA',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label='Date de cloture désirée'
                  type='date'
                  variant='outlined'
                  fullWidth
                  error={undefined !== error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          </Grid>

          {dealTypes.length > 0 && (
            <Grid item xs={12}>
              <Controller
                name='typeId'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field: { value, ...field }, fieldState: { error } }) => (
                  <FormControl {...field} variant='outlined' fullWidth error={undefined !== error}>
                    <InputLabel>Type</InputLabel>
                    <Select label='Type' value={value || ''} native>
                      {dealTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                    {undefined !== error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name='commercialEmployeeId'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field: { value, ...field } }) => (
                <EmployeeSelect
                  label='Employé en charge'
                  value={`${value || ''}`}
                  legacyAgencyId={agencyId}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='customerId'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field }) => <LegacyCustomerAutocomplete {...field} />}
            />
          </Grid>

          {customerId && (
            <Grid item xs={12}>
              <Controller
                name='interlocutorIds'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <InterlocutorsSelect
                    {...field}
                    customerId={customerId}
                    error={undefined !== error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Annuler
        </Button>
        <Button onClick={handleSubmit(onSubmit)} color='primary' autoFocus>
          Dupliquer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDuplicateDeal;
